/**
 * `<EnvironmentPresetMenu />` — INIT-2026-Q3-WEATHER-ENVIRONMENT
 *
 * Preset selector for the Environment card header in `SceneTreePanel`
 * (Zone B.1). Renders a compact chip button that opens a dropdown menu
 * of the six named environment presets.
 *
 * Interaction spec: interaction-designer brief §1.3 (Apply Preset Menu).
 * Visual spec: visual-designer brief §4.5 (Preset Menu surface treatment).
 * Accessibility spec: accessibility-specialist brief §1.1 (Preset Menu).
 *
 * Design-policy compliance:
 *   - No raw hex — all colours via tokens.
 *   - Material Symbols icons (rounded) only.
 *   - 40 px minimum touch targets on all interactive elements.
 *   - role="radiogroup" + role="radio" + aria-checked per a11y brief §1.1.
 *   - Arrow-key navigation between preset items.
 *   - Escape closes menu; focus returns to trigger button.
 *   - No floating panel created — this component renders inline in the
 *     card header area (Zone B.1).
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Icon, tokens, type IconName } from '@/design';
import type { EnvironmentPresetId } from '../catalog';
export type { EnvironmentPresetId };

export interface EnvironmentPresetMenuProps {
  /** Currently active preset. */
  readonly activePreset: EnvironmentPresetId;
  /** Called when the user selects a preset. */
  readonly onSelectPreset: (preset: EnvironmentPresetId) => void;
  /** Called when the user activates the Reset button. */
  readonly onReset: () => void;
}

// ---------------------------------------------------------------------------
// Preset metadata
// ---------------------------------------------------------------------------

interface PresetDef {
  id: EnvironmentPresetId;
  label: string;
  icon: IconName;
  ariaLabel: string;
}

const PRESET_DEFS: readonly PresetDef[] = [
  {
    id: 'clear',
    label: 'Clear day',
    icon: 'wb_sunny' as IconName,
    ariaLabel: 'Clear day preset, sunny midday',
  },
  {
    id: 'overcast',
    label: 'Overcast',
    icon: 'wb_cloudy' as IconName,
    ariaLabel: 'Overcast preset, diffuse cloud cover',
  },
  {
    id: 'dusk',
    label: 'Dusk',
    icon: 'wb_twilight' as IconName,
    ariaLabel: 'Dusk preset, warm low-angle sunlight',
  },
  {
    id: 'night',
    label: 'Night',
    icon: 'nights_stay' as IconName,
    ariaLabel: 'Night preset, dark mode with moon and stars',
  },
  {
    id: 'storm',
    label: 'Storm',
    icon: 'thunderstorm' as IconName,
    ariaLabel: 'Storm preset, heavy clouds wind and rain',
  },
  {
    id: 'custom',
    label: 'Custom',
    icon: 'tune',
    ariaLabel: 'Custom preset, restore last manual configuration',
  },
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EnvironmentPresetMenu({
  activePreset,
  onSelectPreset,
  onReset,
}: EnvironmentPresetMenuProps) {
  const [open, setOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const activeLabel =
    PRESET_DEFS.find((p) => p.id === activePreset)?.label ?? 'Custom';

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const handleTriggerClick = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleSelect = useCallback(
    (presetId: EnvironmentPresetId) => {
      const presetLabel = PRESET_DEFS.find((p) => p.id === presetId)?.label ?? presetId;
      onSelectPreset(presetId);
      setAnnouncement(`Environment preset changed to ${presetLabel}`);
      close();
    },
    [onSelectPreset, close],
  );

  const handleMenuKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const focused = document.activeElement;
        const refs = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
        const idx = refs.indexOf(focused as HTMLButtonElement);
        if (idx === -1) {
          refs[0]?.focus();
          return;
        }
        const next =
          event.key === 'ArrowDown'
            ? refs[(idx + 1) % refs.length]
            : refs[(idx - 1 + refs.length) % refs.length];
        next?.focus();
      }
    },
    [close],
  );

  // Focus the active preset item when the menu opens.
  useEffect(() => {
    if (!open) return;
    const activeIdx = PRESET_DEFS.findIndex((p) => p.id === activePreset);
    const target = itemRefs.current[activeIdx >= 0 ? activeIdx : 0];
    // Defer to next frame so the menu has rendered.
    const timer = setTimeout(() => target?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open, activePreset]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handleOutside = (event: MouseEvent) => {
      const menu = menuRef.current;
      const trigger = triggerRef.current;
      if (!menu || !trigger) return;
      const target = event.target as Node;
      if (!menu.contains(target) && !trigger.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Environment preset: ${activeLabel}. Click to change.`}
        onClick={handleTriggerClick}
        style={triggerStyle(open)}
      >
        <Icon
          name="tune"
          size={16}
          decorative
          style={{ color: tokens.color.ink.secondary }}
        />
        <span
          style={{
            fontSize: tokens.font.role.label.size,
            fontWeight: tokens.font.role.label.weight,
            color: tokens.color.ink.secondary,
            lineHeight: 1,
          }}
        >
          {activeLabel}
        </span>
        <Icon
          name="expand_more"
          size={16}
          decorative
          style={{
            color: tokens.color.ink.muted,
            transform: open ? 'rotate(180deg)' : undefined,
          }}
        />
      </button>

      {/* Dropdown menu */}
      {open ? (
        <div
          ref={menuRef}
          role="radiogroup"
          aria-label="Environment presets"
          onKeyDown={handleMenuKeyDown}
          style={menuContainerStyle()}
        >
          {PRESET_DEFS.map((preset, idx) => {
            const isActive = preset.id === activePreset;
            const isCustomDisabled =
              preset.id === 'custom' && activePreset !== 'custom';

            return (
              <button
                key={preset.id}
                ref={(el) => {
                  itemRefs.current[idx] = el;
                }}
                type="button"
                role="radio"
                aria-checked={isActive}
                aria-label={preset.ariaLabel}
                onClick={() => handleSelect(preset.id)}
                style={menuItemStyle(isActive, isCustomDisabled)}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: tokens.density.touchMin,
                    height: tokens.density.touchMin,
                    flexShrink: 0,
                  }}
                >
                  <Icon
                    name={preset.icon}
                    size={20}
                    decorative
                    style={{
                      color: isActive
                        ? tokens.color.accent.primary
                        : tokens.color.ink.secondary,
                    }}
                  />
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: tokens.font.role.body.size,
                    color: isActive
                      ? tokens.color.accent.primary
                      : tokens.color.ink.primary,
                  }}
                >
                  {preset.label}
                </span>
                {isActive ? (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: tokens.density.touchMin,
                    }}
                  >
                    <Icon
                      name="check_circle"
                      size={16}
                      decorative
                      style={{ color: tokens.color.accent.primary }}
                    />
                  </span>
                ) : (
                  <span style={{ width: tokens.density.touchMin }} />
                )}
              </button>
            );
          })}

          {/* Divider before Reset */}
          <div
            role="separator"
            style={{
              margin: `${tokens.space[1]}px 0`,
              borderTop: tokens.border.thin,
            }}
          />

          {/* Reset button */}
          <button
            type="button"
            aria-label="Reset environment to defaults"
            onClick={() => {
              onReset();
              close();
            }}
            style={resetButtonStyle()}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: tokens.density.touchMin,
                height: tokens.density.touchMin,
                flexShrink: 0,
              }}
            >
              <Icon
                name="restart_alt"
                size={20}
                decorative
                style={{ color: tokens.color.ink.secondary }}
              />
            </span>
            <span
              style={{
                flex: 1,
                fontSize: tokens.font.role.body.size,
                color: tokens.color.ink.secondary,
              }}
            >
              Reset to defaults
            </span>
          </button>
        </div>
      ) : null}

      {/* Screen reader announcement region */}
      <div
        aria-live="polite"
        role="status"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
        }}
      >
        {announcement}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Style helpers — tokens only, no raw constants
// ---------------------------------------------------------------------------

function triggerStyle(open: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    height: tokens.density.touchMin,
    paddingInline: tokens.space[2],
    border: tokens.border.thin,
    borderRadius: tokens.radius.chip,
    background: open ? tokens.color.surface[3] : tokens.color.surface[2],
    cursor: 'pointer',
  };
}

function menuContainerStyle(): CSSProperties {
  return {
    position: 'absolute',
    top: `calc(100% + ${tokens.space[1]}px)`,
    left: 0,
    zIndex: tokens.zIndex.tooltip,
    minWidth: tokens.layout.popoverSm,
    padding: `${tokens.space[1]}px 0`,
    borderRadius: tokens.radius.md,
    border: tokens.border.thin,
    background: tokens.color.surface[2],
    boxShadow: tokens.elevation[3],
  };
}

function menuItemStyle(active: boolean, dimmed: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: tokens.density.touchMin,
    paddingInlineEnd: 0,
    border: 0,
    borderLeft: active
      ? `3px solid ${tokens.color.accent.primary}`
      : '3px solid transparent',
    background: active ? tokens.color.accent.subtle : 'transparent',
    cursor: dimmed ? 'default' : 'pointer',
    opacity: dimmed ? 0.5 : 1,
    textAlign: 'left',
  };
}

function resetButtonStyle(): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: tokens.density.touchMin,
    paddingInlineEnd: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
  };
}
